"use client"

import { useEffect, useState } from "react"
import { AlertTriangle, X } from "lucide-react"

interface OutageStatus {
  outage: boolean
  title?: string
  body?: string
  days?: number
}

const ACCOUNT_BASE = process.env.NEXT_PUBLIC_ACCOUNT_API_URL ?? "https://account.askfinz.ai"
const POLL_INTERVAL = 60_000  // re-check every 60 s

export function OutageBanner() {
  const [status, setStatus] = useState<OutageStatus | null>(null)
  const [dismissed, setDismissed] = useState(false)

  useEffect(() => {
    let timer: ReturnType<typeof setInterval>

    async function check() {
      try {
        const res = await fetch(`${ACCOUNT_BASE}/api/system/status`, { cache: "no-store" })
        if (res.ok) setStatus(await res.json())
      } catch {
        // network error — keep showing existing banner if any
      }
    }

    check()
    timer = setInterval(check, POLL_INTERVAL)
    return () => clearInterval(timer)
  }, [])

  // Re-show if outage becomes active again after being dismissed
  useEffect(() => {
    if (status?.outage) setDismissed(false)
  }, [status?.outage])

  if (!status?.outage || dismissed) return null

  return (
    <div
      role="alert"
      className="fixed top-0 left-0 right-0 z-[200] flex items-start gap-3 bg-amber-50 border-b border-amber-300 px-4 py-3 text-amber-900 dark:bg-amber-950/90 dark:border-amber-700 dark:text-amber-100"
      style={{ zIndex: 200 }}
    >
      <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600 dark:text-amber-400" />
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-sm">
          {status.title ?? "Service Disruption in Progress"}
        </p>
        <p className="mt-1 text-xs leading-relaxed whitespace-pre-line opacity-90">
          {status.body ??
            `We are currently responding to an unplanned outage affecting one of our infrastructure partners. ` +
            `Files from the last ${status.days ?? 7} days linked to your account remain accessible. ` +
            `Our team is actively working to restore full service — your data is safe and fully backed up.`}
        </p>
      </div>
      <button
        onClick={() => setDismissed(true)}
        aria-label="Dismiss notice"
        className="shrink-0 rounded p-1 hover:bg-amber-200/60 dark:hover:bg-amber-800/60 transition-colors"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  )
}
